import React from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import { Badge } from '../../../ui/badge';
import { Card, CardContent } from '../../../ui/card';
import { ChartBlock as ChartBlockType } from '../../../../types/report-builder';

// GO brand palette from design-tokens.json
const BRAND_COLORS = [
  '#4342FF', // Primary blue
  '#924EFF', // Purple
  '#07A0C3', // Teal
  '#92204D', // Berry
  '#80D5FF', // Sky
  '#FFA9E7', // Pink
  '#0003AE', // Sapphire
  '#500091', // Indigo
];

interface Props {
  block: ChartBlockType;
}

/**
 * Try to parse a string as a number, stripping common formatting
 * characters that LLMs add (%, $, £, €, commas, spaces).
 * Returns the number if valid, or undefined if not numeric.
 */
function tryParseNumber(value: string): number | undefined {
  const cleaned = value.replace(/[%$£€,\s]/g, '').trim();
  if (cleaned === '' || cleaned === '-') return undefined;
  const num = Number(cleaned);
  return isNaN(num) ? undefined : num;
}

// Keys that typically hold label strings — never coerce these to numbers
const LABEL_KEYS = new Set(['name', 'label', 'category', 'product', 'outcome', 'range', 'bucket']);

/**
 * Coerce string-encoded numbers to real numbers so Recharts can render them.
 * LLMs often emit formatted values like `"39.9%"`, `"$1,234"`, or `"3812"`
 * instead of raw numbers. Label-candidate keys are left as strings.
 */
function normalizeChartData(data: Record<string, unknown>[]): Record<string, unknown>[] {
  return data.map((item) => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(item)) {
      if (typeof value === 'string' && !LABEL_KEYS.has(key)) {
        const num = tryParseNumber(value);
        out[key] = num !== undefined ? num : value;
      } else {
        out[key] = value;
      }
    }
    return out;
  });
}

/**
 * Auto-detect the name (string) and data (number) keys from the first data item.
 * Falls back to the provided config values or defaults.
 */
function detectKeys(
  data: Record<string, unknown>[],
  configNameKey?: string,
  configDataKey?: string,
): { nameKey: string; dataKey: string } {
  const sample = data[0];
  if (!sample || typeof sample !== 'object') {
    return { nameKey: configNameKey ?? 'name', dataKey: configDataKey ?? 'value' };
  }

  // If config keys exist in the data, trust them
  if (configNameKey && configNameKey in sample && configDataKey && configDataKey in sample) {
    return { nameKey: configNameKey, dataKey: configDataKey };
  }

  const nameCandidates = ['name', 'label', 'category', 'product', 'outcome'];
  const dataCandidates = ['value', 'count', 'total', 'amount', 'percentage'];

  const resolvedName =
    nameCandidates.find((k) => k in sample && typeof sample[k] === 'string') ??
    configNameKey ??
    'name';
  const resolvedData =
    dataCandidates.find((k) => k in sample && typeof sample[k] === 'number') ??
    // Fallback: any numeric key that isn't the name key
    Object.keys(sample).find((k) => k !== resolvedName && typeof sample[k] === 'number') ??
    configDataKey ??
    'value';

  return { nameKey: resolvedName, dataKey: resolvedData };
}

/**
 * Prepare data for multi-series line/area charts.
 *
 * Handles two data shapes the LLM may produce:
 *
 * **Wide format** (already pivoted — one row per x-axis tick):
 *   [{ year: "2023", "Company A": 120, "Company B": 95 }, ...]
 *   → returned as-is with seriesKeys = ["Company A", "Company B"]
 *
 * **Long/flat format** (one row per series per tick):
 *   [{ year: "2023", company: "Company A", decisions: 120 }, ...]
 *   → pivoted to wide format, seriesKeys = unique company values
 */
function prepareSeriesData(
  data: Record<string, unknown>[],
  xAxisKey: string,
): { pivotedData: Record<string, unknown>[]; seriesKeys: string[] } {
  const sample = data[0];
  if (!sample || typeof sample !== 'object') {
    return { pivotedData: data, seriesKeys: [] };
  }

  // Wide format: multiple numeric keys already present
  const numericKeys = Object.keys(sample).filter(
    (k) => k !== xAxisKey && typeof sample[k] === 'number',
  );
  if (numericKeys.length > 1) {
    return { pivotedData: data, seriesKeys: numericKeys };
  }

  // Long format: exactly 1 numeric key + 1 other string key (the group column)
  const stringKeys = Object.keys(sample).filter(
    (k) => k !== xAxisKey && typeof sample[k] === 'string',
  );
  if (numericKeys.length === 1 && stringKeys.length >= 1) {
    const valueKey = numericKeys[0];
    const groupKey = stringKeys[0];

    // Check for repeated x-axis values (confirms long format)
    const xValues = data.map((d) => d[xAxisKey]);
    const hasDuplicateX = new Set(xValues).size < xValues.length;
    if (!hasDuplicateX) {
      // No duplicates — single series, return as-is
      return { pivotedData: data, seriesKeys: [valueKey] };
    }

    // Pivot: group by x-axis, spread group values into columns
    const seriesNames = Array.from(new Set(data.map((d) => String(d[groupKey]))));
    const grouped: Record<string, Record<string, unknown>> = {};
    for (const row of data) {
      const xVal = String(row[xAxisKey]);
      if (!grouped[xVal]) {
        grouped[xVal] = { [xAxisKey]: row[xAxisKey] };
      }
      grouped[xVal][String(row[groupKey])] = row[valueKey];
    }

    return {
      pivotedData: Object.values(grouped),
      seriesKeys: seriesNames,
    };
  }

  // Fallback: single series
  return { pivotedData: data, seriesKeys: numericKeys.length > 0 ? numericKeys : [] };
}

/** Calculate dynamic chart height based on chart type and data count. */
function getChartHeight(chartType: string, dataCount: number): number {
  if (chartType === 'horizontal_bar') {
    // 44px per bar, minimum 200, maximum 600
    return Math.min(600, Math.max(200, dataCount * 44));
  }
  if (chartType === 'donut') {
    return 280;
  }
  // bar, line, area
  return 300;
}

const GRID_STYLE = { stroke: 'hsl(var(--border))', strokeOpacity: 0.5 };
const AXIS_STYLE = { fontSize: 12, fill: 'hsl(var(--muted-foreground))' };

const ChartBlock: React.FC<Props> = ({ block }) => {
  const data = normalizeChartData(Array.isArray(block.data) ? block.data : []);
  // Always use GO brand palette for consistent visual identity
  const colors = BRAND_COLORS;
  const { nameKey, dataKey } = detectKeys(
    data as Record<string, unknown>[],
    block.config?.nameKey,
    block.config?.dataKey,
  );
  const xAxis = block.config?.xAxis ?? nameKey;
  const yAxis = block.config?.yAxis ?? dataKey;
  const chartHeight = getChartHeight(block.chartType, data.length);

  const renderChart = () => {
    switch (block.chartType) {
      case 'donut':
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={dataKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={105}
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((_, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 13,
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
            />
          </PieChart>
        );

      case 'bar': {
        // When config explicitly specifies yAxis, use single-series rendering
        // to avoid treating ancillary numeric fields (e.g. percentage) as extra series.
        const hasExplicitY = !!(block.config?.yAxis || block.config?.dataKey);
        const { pivotedData: barData, seriesKeys: barSeries } = prepareSeriesData(
          data as Record<string, unknown>[], xAxis,
        );
        const barKeys = hasExplicitY ? [yAxis] : (barSeries.length > 0 ? barSeries : [yAxis]);
        const isMultiSeries = barKeys.length > 1;
        return (
          <BarChart data={isMultiSeries ? barData : data} barSize={32}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" {...GRID_STYLE} />
            <XAxis dataKey={xAxis} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 13,
              }}
            />
            {isMultiSeries ? (
              <>
                <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
                {barKeys.map((key, i) => (
                  <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} />
                ))}
              </>
            ) : (
              <Bar dataKey={yAxis} radius={[4, 4, 0, 0]}>
                {data.map((_, index) => (
                  <Cell key={index} fill={colors[index % colors.length]} />
                ))}
              </Bar>
            )}
          </BarChart>
        );
      }

      case 'horizontal_bar':
        return (
          <BarChart data={data} layout="vertical" barSize={24} margin={{ left: 8, right: 24 }}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" {...GRID_STYLE} />
            <XAxis type="number" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis
              dataKey={nameKey}
              type="category"
              width={200}
              tick={AXIS_STYLE}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 13,
              }}
            />
            <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
              {data.map((_, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        );

      case 'line': {
        const { pivotedData: lineData, seriesKeys: lineSeries } = prepareSeriesData(
          data as Record<string, unknown>[], xAxis,
        );
        const lineKeys = lineSeries.length > 0 ? lineSeries : [yAxis];
        return (
          <LineChart data={lineData}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" {...GRID_STYLE} />
            <XAxis dataKey={xAxis} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 13,
              }}
            />
            {lineKeys.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
            )}
            {lineKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
              />
            ))}
          </LineChart>
        );
      }

      case 'area': {
        const { pivotedData: areaData, seriesKeys: areaSeries } = prepareSeriesData(
          data as Record<string, unknown>[], xAxis,
        );
        const areaKeys = areaSeries.length > 0 ? areaSeries : [yAxis];
        return (
          <AreaChart data={areaData}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" {...GRID_STYLE} />
            <XAxis dataKey={xAxis} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid hsl(var(--border))',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 13,
              }}
            />
            {areaKeys.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
            )}
            {areaKeys.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length]}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        );
      }

      default:
        return (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Unsupported chart type
          </div>
        );
    }
  };

  return (
    <Card className="my-6 overflow-hidden border-border/70 bg-card shadow-[var(--shadow-card)]">
      <CardContent className="space-y-4 py-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{block.title}</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Visual summary for the current report section.
            </p>
          </div>
          <Badge variant="secondary">Chart</Badge>
        </div>

        <div style={{ height: chartHeight }} className="w-full">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-border/70 bg-[var(--go-spring)]/50 text-sm text-muted-foreground">
            No chart data available for this section yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartBlock;
